import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../../../services/apiService';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Input,
  Select,
  DatePicker,
  Modal,
  Form,
  message,
  Descriptions,
  Tabs,
  Statistic,
  Badge,
  Tooltip,
  InputNumber,
  Divider,
  List,
  Alert,
  Popconfirm,
  Menu,
  Checkbox,
  Dropdown,
  Timeline,
} from 'antd';
import {
  StockOutlined,
  EyeOutlined,
  EditOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined,
  BarChartOutlined,
  InboxOutlined,
  SendOutlined,
  HistoryOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  AuditOutlined,
  UserOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import useDraggableColumns from '../../../components/table/useDraggableColumns';


const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// 各倉庫庫存明細接口
interface WarehouseInventory {
  id: string;
  warehouseId: string;
  warehouseName: string;
  total: number;
  available: number;
  shipping: number;
  locationCode?: string;
  updatedAt: string;
}

// 貨位分佈明細接口
interface LocationDetail {
  orderNumber: string;
  lotNumber: string;
  expirationDate?: string;
  locationPath: string;
  quantity: number;
  updatedAt: string;
}

// 庫存數據接口 - 基於Excel字段
interface StockItem {
  id: string;
  ownerAccount: string; // 貨主賬號
  sku: string;
  barcode: string;
  productName: string; // 產品名稱
  updateTime: string; // 更新時間
  stockQuantity: number; // 庫存數量（總計）
  availableQuantity: number; // 可下單數量（總計）
  outboundQuantity: number; // 出庫中數量
  expectedQuantity: number; // 預期數量（來自入庫單）
  occupiedCBM: number; // 佔用庫存CBM
  lastInboundOrder: string; // 最後修改-入庫單
  lastInboundQuantity: number; // 最後修改-入庫數量
  lastOutboundOrder: string; // 最後修改-發貨單
  lastOutboundQuantity: number; // 最後修改-發貨數量
  
  // 額外字段
  warehouse: string; // 保留用於調整申請
  category: string;
  unitPrice: number;
  totalValue: number;
  reorderLevel: number;
  supplier: string;
  status: 'normal' | 'low_stock' | 'out_of_stock' | 'excess';
  alternateSkuList?: string[]; // 備用 SKU 列表
  
  // 各倉庫庫存明細（詳情頁使用）
  warehouses?: WarehouseInventory[];
}

// 相關訂單接口
interface RelatedOrder {
  id: string;
  orderNumber: string;
  type: 'inbound' | 'outbound' | 'transfer';
  date: string;
  quantity: number;
  status: string;
  reference?: string;
}

// 庫存調整接口
interface StockAdjustment {
  id: string;
  sku: string;
  productName: string;
  currentQuantity: number;
  adjustmentQuantity: number;
  newQuantity: number;
  reason: string;
  reasonCode: string;
  notes?: string;
  requestedBy: string;
  requestedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  status: 'pending' | 'approved' | 'rejected';
  approvalReason?: string;
  warehouse: string;
  location: string;
  urgency: 'low' | 'normal' | 'high' | 'critical';
  alternateSkuList?: string[]; // 添加備用 SKU 列表
}

// 表格列配置類型
type ColumnSetting = { visible: boolean; order: number };
type StockColumnKey = 'sku' | 'productName' | 'barcode' | 'stockQuantity' | 'availableQuantity' | 'outboundQuantity' | 'expectedQuantity' | 'occupiedCBM' | 'lastInboundOrder' | 'lastOutboundOrder' | 'updateTime' | 'status' | 'actions';
type StockColumnSettings = Record<StockColumnKey, ColumnSetting>;
type AdjustmentColumnKey = 'sku' | 'productName' | 'currentQuantity' | 'adjustmentQuantity' | 'newQuantity' | 'reason' | 'notes' | 'requestedBy' | 'requestedAt' | 'reviewedBy' | 'reviewedAt' | 'urgency' | 'status' | 'actions';
type AdjustmentColumnSettings = Record<AdjustmentColumnKey, ColumnSetting>;

const StockManagement: React.FC = () => {
  // 狀態管理
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  // 先定義排序狀態，避免被閉包在初始化前引用
  const [stockSortField, setStockSortField] = useState<string>('');
  const [stockSortOrder, setStockSortOrder] = useState<'asc' | 'desc' | ''>('');
  // 受控分頁狀態（對齊 Products 實現）
  const [stockCurrentPage, setStockCurrentPage] = useState<number>(1);
  const [stockPageSize, setStockPageSize] = useState<number>(20);
  const [stockTotal, setStockTotal] = useState<number>(0);
  // 篩選狀態（需在 loadStock 之前定義，避免 TDZ）
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const locationHook = useLocation();

  // 從後端拉取庫存列表
  const loadStock = useCallback(async (page = 1, limit = 20) => {
    try {
      setLoading(true);
      const resp: any = await apiService.getStockList({
        page,
        limit,
        sortField: stockSortField || 'updatedAt',
        sortOrder: (stockSortOrder as any) || 'desc',
        search: (searchText || '').trim() || undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
      });
      const list = (resp?.data || []).map((r: any) => ({
        id: r.id,
        ownerAccount: r.supplier || 'Default',
        sku: r.itemCode,
        barcode: r.barcode || '',
        productName: r.itemName,
        updateTime: r.updatedAt,
        stockQuantity: Number(r.total || 0),
        availableQuantity: Number(r.available || 0),
        outboundQuantity: Number(r.shipping || 0),
        expectedQuantity: Number(r.expectedQuantity || r.pendingInQuantity || 0),
        occupiedCBM: 0,
        lastInboundOrder: '',
        lastInboundQuantity: 0,
        lastOutboundOrder: '',
        lastOutboundQuantity: 0,
        warehouse: '', // 列表不显示仓库名称
        category: r.category || '',
        unitPrice: 0,
        totalValue: 0,
        reorderLevel: 0,
        supplier: r.supplier || '',
        status: Number(r.total || 0) === 0 ? 'out_of_stock' : (Number(r.available || 0) <= 0 ? 'low_stock' : 'normal'),
        alternateSkuList: Array.isArray(r.alternateSkuList) ? r.alternateSkuList : []  // 添加備用 SKU 列表
      }));
      setStockData(list);
      const total = resp?.pagination?.total ?? list.length;
      setStockTotal(total);
      setStockCurrentPage(page);
      setStockPageSize(limit);
    } catch (e) {
      setStockData([]);
    } finally {
      setLoading(false);
    }
  }, [stockSortField, stockSortOrder, searchText]);

  useEffect(() => {
    // 初始化時讀取 URL 參數中的 sku 作為預設搜尋
    try {
      const usp = new URLSearchParams(locationHook.search);
      const skuParam = usp.get('sku');
      if (skuParam && skuParam.trim().length > 0) {
        setSearchText(skuParam.trim());
      }
    } catch {}
    loadStock(stockCurrentPage, stockPageSize);
  }, [loadStock, stockCurrentPage, stockPageSize]);

  // 搜尋/排序變更時，防抖請求後端
  const debounceRef = useRef<any>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadStock(1, stockPageSize);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText, stockSortField, stockSortOrder, stockPageSize]);
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [adjustmentVisible, setAdjustmentVisible] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [relatedOrders, setRelatedOrders] = useState<RelatedOrder[]>([]);
  const [productLocations, setProductLocations] = useState<LocationDetail[]>([]);
  const [adjustmentForm] = Form.useForm();
  // 本地回顯緩存：按庫存項ID保存最近一次編輯內容
  const [lastEdits, setLastEdits] = useState<Record<string, { adjustmentQuantity: number; reason: string; notes: string; newQuantity: number; currentQuantity: number }>>({});

  // 庫存表格排序狀態（已提前定義）

  // 調整申請表格排序狀態
  const [adjustmentSortField, setAdjustmentSortField] = useState<string>('');
  const [adjustmentSortOrder, setAdjustmentSortOrder] = useState<'asc' | 'desc' | ''>('');

  // 庫存表格列配置狀態
  const [stockColumnSettings, setStockColumnSettings] = useState<StockColumnSettings>(() => {
    const saved = localStorage.getItem('stock-management-columns-config');
    const version = localStorage.getItem('stock-management-columns-version');
    
    // 檢查版本，如果不是最新版本則重置配置
    if (version !== '2024-01-v4' || !saved) {
      const defaultConfig: StockColumnSettings = {
        sku: { visible: true, order: 0 },
        productName: { visible: true, order: 1 },
        barcode: { visible: true, order: 2 },
        stockQuantity: { visible: true, order: 3 },
        availableQuantity: { visible: true, order: 4 },
        outboundQuantity: { visible: true, order: 5 },
        expectedQuantity: { visible: true, order: 6 },
        occupiedCBM: { visible: false, order: 7 },
        lastInboundOrder: { visible: false, order: 8 },
        lastOutboundOrder: { visible: false, order: 9 },
        updateTime: { visible: true, order: 10 },
        status: { visible: true, order: 11 },
        actions: { visible: true, order: 12 }
      };
      localStorage.setItem('stock-management-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('stock-management-columns-version', '2024-01-v4');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved) as StockColumnSettings;
    } catch {
      const defaultConfig: StockColumnSettings = {
        sku: { visible: true, order: 0 },
        productName: { visible: true, order: 1 },
        barcode: { visible: true, order: 2 },
        stockQuantity: { visible: true, order: 3 },
        availableQuantity: { visible: true, order: 4 },
        outboundQuantity: { visible: true, order: 5 },
        expectedQuantity: { visible: true, order: 6 },
        occupiedCBM: { visible: false, order: 7 },
        lastInboundOrder: { visible: false, order: 8 },
        lastOutboundOrder: { visible: false, order: 9 },
        updateTime: { visible: true, order: 10 },
        status: { visible: true, order: 11 },
        actions: { visible: true, order: 12 }
      };
      return defaultConfig;
    }
  });

  // 調整申請表格列配置狀態
  const [adjustmentColumnSettings, setAdjustmentColumnSettings] = useState<AdjustmentColumnSettings>(() => {
    const saved = localStorage.getItem('adjustment-audit-columns-config');
    const version = localStorage.getItem('adjustment-audit-columns-version');
    
    // 檢查版本，如果不是最新版本則重置配置
    if (version !== '2024-01-v2' || !saved) {
      const defaultConfig: AdjustmentColumnSettings = {
        sku: { visible: true, order: 0 },
        productName: { visible: true, order: 1 },
        currentQuantity: { visible: true, order: 2 },
        adjustmentQuantity: { visible: true, order: 3 },
        newQuantity: { visible: true, order: 4 },
        reason: { visible: true, order: 5 },
        notes: { visible: false, order: 6 },
        requestedBy: { visible: true, order: 7 },
        requestedAt: { visible: true, order: 8 },
        reviewedBy: { visible: false, order: 9 },
        reviewedAt: { visible: false, order: 10 },
        urgency: { visible: true, order: 11 },
        status: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('adjustment-audit-columns-version', '2024-01-v2');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved) as AdjustmentColumnSettings;
    } catch {
      const defaultConfig: AdjustmentColumnSettings = {
        sku: { visible: true, order: 0 },
        productName: { visible: true, order: 1 },
        currentQuantity: { visible: true, order: 2 },
        adjustmentQuantity: { visible: true, order: 3 },
        newQuantity: { visible: true, order: 4 },
        reason: { visible: true, order: 5 },
        notes: { visible: false, order: 6 },
        requestedBy: { visible: true, order: 7 },
        requestedAt: { visible: true, order: 8 },
        reviewedBy: { visible: false, order: 9 },
        reviewedAt: { visible: false, order: 10 },
        urgency: { visible: true, order: 11 },
        status: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      return defaultConfig;
    }
  });

  // 處理列排序
  const handleStockColumnSort = (columnKey: string) => {
    if (stockSortField === columnKey) {
      setStockSortOrder(stockSortOrder === 'asc' ? 'desc' : stockSortOrder === 'desc' ? '' : 'asc');
      if (stockSortOrder === 'desc') {
        setStockSortField('');
      }
    } else {
      setStockSortField(columnKey);
      setStockSortOrder('asc');
    }
  };

  // 獲取排序圖標
  const getStockSortIcon = (columnKey: string) => {
    if (stockSortField !== columnKey) return null;
    return stockSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理調整申請列排序
  const handleAdjustmentColumnSort = (columnKey: string) => {
    if (adjustmentSortField === columnKey) {
      setAdjustmentSortOrder(adjustmentSortOrder === 'asc' ? 'desc' : adjustmentSortOrder === 'desc' ? '' : 'asc');
      if (adjustmentSortOrder === 'desc') {
        setAdjustmentSortField('');
      }
    } else {
      setAdjustmentSortField(columnKey);
      setAdjustmentSortOrder('asc');
    }
  };

  // 獲取調整申請排序圖標
  const getAdjustmentSortIcon = (columnKey: string) => {
    if (adjustmentSortField !== columnKey) return null;
    return adjustmentSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };
  
  // Stock Adjustment相關狀態
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [selectedAdjustment, setSelectedAdjustment] = useState<StockAdjustment | null>(null);
  const [adjustmentDetailsVisible, setAdjustmentDetailsVisible] = useState(false);
  const [approvalVisible, setApprovalVisible] = useState(false);
  const [approvalForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('stock');
  // 列設置下拉菜單可見狀態
  const [stockColumnSettingsVisible, setStockColumnSettingsVisible] = useState(false);
  // 調整審核分頁狀態
  const [adjustmentCurrentPage, setAdjustmentCurrentPage] = useState<number>(1);
  const [adjustmentPageSize, setAdjustmentPageSize] = useState<number>(20);
  const [adjustmentTotal, setAdjustmentTotal] = useState<number>(0);
  // 調整審核篩選
  const [adjustmentSearchSku, setAdjustmentSearchSku] = useState<string>('');
  const [adjustmentStatus, setAdjustmentStatus] = useState<string>('all');
  
  // 多選狀態管理
  const [selectedAdjustmentIds, setSelectedAdjustmentIds] = useState<string[]>([]);
  const [batchApprovalVisible, setBatchApprovalVisible] = useState(false);
  const [batchApprovalForm] = Form.useForm();


  // 篩選狀態（已提前定義於頂部）

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 移除模擬庫存數據，統一由後端 API 載入

  // 移除本地相關訂單模擬數據，改為後端加載或保持空

  // Stock Adjustment模拟數據
  const mockAdjustments: StockAdjustment[] = [];

  // 庫存調整原因選項
  const adjustmentReasons = [
    { value: 'cycle_count', label: '週期盤點調整' },
    { value: 'damage', label: '貨物損壞' },
    { value: 'expired', label: '貨物過期' },
    { value: 'lost', label: '貨物丟失' },
    { value: 'found', label: '發現額外庫存' },
    { value: 'return', label: '退貨入庫' },
    { value: 'correction', label: '系統錯誤修正' },
    { value: 'other', label: '其他原因' }
  ];

  useEffect(() => {
    loadStock(1, 20);
    loadAdjustments();
  }, [loadStock]);

  // 移除本地 mock 加載函式，改為 loadStock 由後端載入

  const loadAdjustments = async (page = 1, limit = 20, status?: string) => {
    try {
      const resp: any = await apiService.getStockAdjustments({ 
        page, 
        limit, 
        status: (status ?? adjustmentStatus) && (status ?? adjustmentStatus) !== 'all' ? (status ?? adjustmentStatus) as any : undefined,
        sku: (adjustmentSearchSku || '').trim() || undefined
      });
      const list: StockAdjustment[] = (resp?.data || []).map((r: any) => ({
        id: r.id,
        sku: r.sku,
        productName: r.productName,
        currentQuantity: Number(r.currentQuantity || 0),
        adjustmentQuantity: Number(r.adjustmentQuantity || 0),
        newQuantity: Number(r.newQuantity || 0),
        reason: r.reason || '',
        reasonCode: r.reasonCode || 'adjustment',
        notes: r.notes || '',
        requestedBy: r.requestedBy || 'System',
        requestedAt: r.requestedAt || '',
        reviewedBy: r.reviewedBy || undefined,
        reviewedAt: r.reviewedAt || undefined,
        status: r.status || 'pending',
        warehouse: r.warehouse || '',
        location: r.location || '',
        urgency: r.urgency || 'normal',
        alternateSkuList: Array.isArray(r.alternateSkuList) ? r.alternateSkuList : [] // 添加備用 SKU 列表
      }));
      setAdjustments(list);
      const total = resp?.pagination?.total ?? list.length;
      setAdjustmentTotal(total);
      setAdjustmentCurrentPage(page);
      setAdjustmentPageSize(limit);
    } catch (error) {
      message.error('載入調整申請失敗');
    }
  };

  // 調整審核查詢條件變更後，防抖刷新
  useEffect(() => {
    const t = setTimeout(() => {
      loadAdjustments(1, adjustmentPageSize);
    }, 300);
    return () => clearTimeout(t);
  }, [adjustmentSearchSku, adjustmentStatus, adjustmentPageSize]);

  const handleViewDetails = async (stock: StockItem) => {
    setDetailsVisible(true);
    setDetailsLoading(true);
    try {
      const current = stockData.find(s => s.id === stock.id) || stock;
      const resp: any = await apiService.getStockItem(stock.id).catch(() => null);
      
      // 获取产品的所有货位信息（详细列表）
      let productLocations: LocationDetail[] = [];
      try {
        const locationsResp: any = await apiService.get(`/locations/products/${encodeURIComponent(stock.sku)}`);
        productLocations = locationsResp?.locations || [];
      } catch (error) {
        console.error('Failed to fetch product locations:', error);
      }
      
      if (resp) {
        const item: StockItem = {
          id: resp.id || current.id,
          ownerAccount: resp.supplier ?? current.ownerAccount,
          sku: resp.itemCode ?? current.sku,
          barcode: resp.barcode ?? current.barcode,
          productName: resp.itemName ?? current.productName,
          updateTime: resp.updatedAt ?? current.updateTime,
          stockQuantity: Number(resp.total ?? current.stockQuantity ?? 0),
          availableQuantity: Number(resp.available ?? current.availableQuantity ?? 0),
          outboundQuantity: Number(resp.shipping ?? current.outboundQuantity ?? 0),
          expectedQuantity: Number(resp.expectedQuantity ?? resp.pendingInQuantity ?? current.expectedQuantity ?? 0),
          occupiedCBM: current.occupiedCBM || 0,
          lastInboundOrder: current.lastInboundOrder || '',
          lastInboundQuantity: current.lastInboundQuantity || 0,
          lastOutboundOrder: current.lastOutboundOrder || '',
          lastOutboundQuantity: current.lastOutboundQuantity || 0,
          warehouse: resp.warehouseName ?? current.warehouse ?? '',
          category: resp.category ?? current.category ?? '',
          unitPrice: current.unitPrice || 0,
          totalValue: current.totalValue || 0,
          reorderLevel: current.reorderLevel || 0,
          supplier: resp.supplier ?? current.supplier ?? '',
          status: Number((resp.total ?? current.stockQuantity) ?? 0) === 0
            ? 'out_of_stock'
            : (Number((resp.available ?? current.availableQuantity) ?? 0) <= 0 ? 'low_stock' : 'normal'),
          alternateSkuList: Array.isArray(resp.alternateSkuList) ? resp.alternateSkuList : []  // 添加備用 SKU 列表
        };
        setSelectedStock(item);
        setRelatedOrders(resp.relatedOrders || resp.orders || []);
        setProductLocations(productLocations);
      } else {
        setSelectedStock({
          ...current,
          alternateSkuList: Array.isArray(current.alternateSkuList) ? current.alternateSkuList : []  // 添加備用 SKU 列表
        });
        setRelatedOrders([]);
        setProductLocations(productLocations);
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleStockAdjustment = async (stock: StockItem) => {
    setAdjustmentVisible(true);
    setAdjustmentLoading(true);
    try {
      const current = stockData.find(s => s.id === stock.id) || stock;
      const resp: any = await apiService.getStockItem(stock.id).catch(() => null);
      const base = resp ? {
        id: resp.id || current.id,
        sku: resp.itemCode || current.sku,
        productName: resp.itemName || current.productName,
        stockQuantity: Number(resp.total ?? current.stockQuantity ?? 0),
        warehouse: resp.warehouseName || current.warehouse || '',
        alternateSkuList: Array.isArray(resp.alternateSkuList) ? resp.alternateSkuList : []  // 添加備用 SKU 列表
      } : {
        id: current.id,
        sku: current.sku,
        productName: current.productName,
        stockQuantity: current.stockQuantity,
        warehouse: current.warehouse,
        alternateSkuList: Array.isArray(current.alternateSkuList) ? current.alternateSkuList : []  // 添加備用 SKU 列表
      };
      setSelectedStock({
        ...stock,
        warehouse: base.warehouse,
        stockQuantity: base.stockQuantity
      });
      const cached = lastEdits[base.id];
      adjustmentForm.setFieldsValue({
        sku: base.sku,
        productName: base.productName,
        currentQuantity: cached?.currentQuantity ?? base.stockQuantity,
        adjustmentQuantity: cached?.adjustmentQuantity ?? 0,
        newQuantity: cached?.newQuantity ?? base.stockQuantity,
        reason: cached?.reason ?? '',
        notes: cached?.notes ?? ''
      });
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const handleAdjustmentSubmit = async (values: any) => {
    try {
      setAdjustmentLoading(true);
      const adjustmentData: Partial<StockAdjustment> = {
        sku: selectedStock?.sku,
        productName: selectedStock?.productName,
        currentQuantity: selectedStock?.stockQuantity || 0,
        adjustmentQuantity: values.adjustmentQuantity,
        newQuantity: (selectedStock?.stockQuantity || 0) + values.adjustmentQuantity,
        reason: adjustmentReasons.find(r => r.value === values.reason)?.label || '',
        reasonCode: values.reason,
        notes: values.notes,
        requestedBy: 'Current User',
        requestedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
        status: 'pending',
        warehouse: selectedStock?.warehouse || '',
        location: '',
        urgency: 'normal'
      };

      await apiService.updateStockItem(selectedStock!.id, {
        adjustmentQuantity: values.adjustmentQuantity,
        reasonCode: values.reason,
        notes: values.notes
      }).catch(async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
      });
      
      // 立即刷新一次調整列表，獲取後端生成的真實調整ID
      await loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);
      setStockData(prev => prev.map(item =>
        item.id === selectedStock!.id
          ? { ...item, stockQuantity: Number((adjustmentData as any).newQuantity || 0), availableQuantity: Math.max(0, (item.availableQuantity || 0) + values.adjustmentQuantity), updateTime: moment().format('YYYY-MM-DD HH:mm:ss') }
          : item
      ));
      // 保存本地回顯緩存
      setLastEdits(prev => ({
        ...prev,
        [selectedStock!.id]: {
          adjustmentQuantity: values.adjustmentQuantity,
          reason: values.reason,
          notes: values.notes,
          newQuantity: (selectedStock?.stockQuantity || 0) + values.adjustmentQuantity,
          currentQuantity: selectedStock?.stockQuantity || 0
        }
      }));
      // 不立即重拉後端，避免覆蓋本地最新值；稍後或頁面切換時再同步
      
      message.success('庫存調整申請已提交，等待審核');
      setAdjustmentVisible(false);
      adjustmentForm.resetFields();
    } catch (error) {
      message.error('提交庫存調整失敗');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Stock Adjustment相關處理函數
  const handleViewAdjustmentDetails = (adjustment: StockAdjustment) => {
    setSelectedAdjustment(adjustment);
    setAdjustmentDetailsVisible(true);
  };

  const handleApproval = (adjustment: StockAdjustment, action: 'approve' | 'reject') => {
    setSelectedAdjustment(adjustment);
    approvalForm.setFieldsValue({
      action,
      reason: ''
    });
    setApprovalVisible(true);
  };

  const handleApprovalSubmit = async (values: any) => {
    try {
      // 呼叫後端 API
      await apiService.processStockAdjustment(selectedAdjustment!.id, {
        action: values.action,
        reason: values.reason,
        reviewedBy: 'Current User'
      });

      // 成功後刷新列表（保持當前分頁）
      await loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);

      message.success(`調整申請已${values.action === 'approve' ? '批準' : '拒絕'}`);
      setApprovalVisible(false);
      approvalForm.resetFields();
    } catch (error) {
      message.error('處理審核失敗');
    }
  };

  // 多選處理函數
  const handleBatchSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedAdjustmentIds(selectedRowKeys as string[]);
  };

  const handleBatchApproval = (action: 'approve' | 'reject') => {
    if (selectedAdjustmentIds.length === 0) {
      message.warning('請先選擇要處理的調整申請');
      return;
    }
    
    batchApprovalForm.setFieldsValue({
      action,
      reason: ''
    });
    setBatchApprovalVisible(true);
  };

  const handleBatchApprovalSubmit = async (values: any) => {
    try {
      await apiService.batchProcessStockAdjustments({
        action: values.action,
        ids: selectedAdjustmentIds,
        reason: values.reason,
        reviewedBy: 'Current User'
      });

      await loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);

      message.success(`已${values.action === 'approve' ? '批準' : '拒絕'} ${selectedAdjustmentIds.length} 個調整申請`);
      setBatchApprovalVisible(false);
      setSelectedAdjustmentIds([]);
      batchApprovalForm.resetFields();
    } catch (error) {
      message.error('批量處理審核失敗');
    }
  };



  const handleExportExcel = () => {
    // 模擬Excel導出
    const csvContent = generateCSVContent(filteredData);
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `stock_report_${moment().format('YYYYMMDD_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('庫存報表已導出');
  };

  const generateCSVContent = (data: StockItem[]) => {
    const headers = [
      '貨主賬號', 'SKU', '條碼', '產品名稱', '更新時間',
      '庫存數量', '可下單數量', '出庫中數量', '預期數量', '佔用庫存CBM',
      '最後修改-入庫單', '最後修改-入庫數量', '最後修改-發貨單', '最後修改-發貨數量'
    ];
    
    const rows = data.map(item => [
      item.ownerAccount,
      item.sku,
      item.barcode,
      item.productName,
      item.updateTime ? `\u200B${new Date(item.updateTime).toISOString().split('T')[0]}` : '',
      item.stockQuantity,
      item.availableQuantity,
      item.outboundQuantity,
      item.expectedQuantity,
      item.occupiedCBM,
      item.lastInboundOrder,
      item.lastInboundQuantity,
      item.lastOutboundOrder,
      item.lastOutboundQuantity
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 篩選數據
  const filteredData = stockData.filter(item => {
    const matchesSearch = !searchText || 
      item.sku.toLowerCase().includes(searchText.toLowerCase()) ||
      item.productName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.barcode.includes(searchText) ||
      (item.alternateSkuList && item.alternateSkuList.some(sku => sku.toLowerCase().includes(searchText.toLowerCase())));
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  // 定義所有可用的庫存列
  const allStockColumns = {
    sku: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('sku')}
        >
          SKU
          {getStockSortIcon('sku')}
        </div>
      ),
      dataIndex: 'sku',
      key: 'sku',
      width: 220,
      fixed: 'left' as 'left',
      sorter: true,
      render: (text: string) => <Text code strong>{text}</Text>
    },
    productName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('productName')}
        >
          產品名稱
          {getStockSortIcon('productName')}
        </div>
      ),
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      ellipsis: { showTitle: false },
      sorter: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      )
    },
    barcode: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('barcode')}
        >
          條碼
          {getStockSortIcon('barcode')}
        </div>
      ),
      dataIndex: 'barcode',
      key: 'barcode',
      width: 140,
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },
    stockQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('stockQuantity')}
        >
          庫存數量
          {getStockSortIcon('stockQuantity')}
        </div>
      ),
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number, record: StockItem) => (
        <div>
          <Text strong style={{ 
            color: record.status === 'low_stock' ? '#faad14' : 
                   record.status === 'out_of_stock' ? '#ff4d4f' : '#52c41a'
          }}>
            {value}
          </Text>
        </div>
      )
    },
    availableQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('availableQuantity')}
        >
          可下單數量
          {getStockSortIcon('availableQuantity')}
        </div>
      ),
      dataIndex: 'availableQuantity',
      key: 'availableQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => (
        <Text style={{
          backgroundColor: 'rgba(82, 196, 26, 0.15)',
          color: '#389e0d',
          padding: '2px 6px',
          borderRadius: 4,
          display: 'inline-block',
          minWidth: 32,
          textAlign: 'right'
        }}>
          {value}
        </Text>
      )
    },
    outboundQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('outboundQuantity')}
        >
          出庫中數量
          {getStockSortIcon('outboundQuantity')}
        </div>
      ),
      dataIndex: 'outboundQuantity',
      key: 'outboundQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => value > 0 ? <Badge count={value} color="orange" /> : <Text>0</Text>
    },
    expectedQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('expectedQuantity')}
        >
          預期數量
          {getStockSortIcon('expectedQuantity')}
        </div>
      ),
      dataIndex: 'expectedQuantity',
      key: 'expectedQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => (
        <Text style={{
          backgroundColor: value > 0 ? 'rgba(24, 144, 255, 0.15)' : 'rgba(0, 0, 0, 0.04)',
          color: value > 0 ? '#0958d9' : '#999',
          padding: '2px 6px',
          borderRadius: 4,
          display: 'inline-block',
          minWidth: 32,
          textAlign: 'right'
        }}>
          {value}
        </Text>
      )
    },
    occupiedCBM: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('occupiedCBM')}
        >
          佔用CBM
          {getStockSortIcon('occupiedCBM')}
        </div>
      ),
      dataIndex: 'occupiedCBM',
      key: 'occupiedCBM',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => <Text>{value.toFixed(2)}</Text>
    },
    lastInboundOrder: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('lastInboundOrder')}
        >
          最後入庫單
          {getStockSortIcon('lastInboundOrder')}
        </div>
      ),
      dataIndex: 'lastInboundOrder',
      key: 'lastInboundOrder',
      width: 140,
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },
    lastOutboundOrder: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('lastOutboundOrder')}
        >
          最後發貨單
          {getStockSortIcon('lastOutboundOrder')}
        </div>
      ),
      dataIndex: 'lastOutboundOrder',
      key: 'lastOutboundOrder',
      width: 140,
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },
    updateTime: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('updateTime')}
        >
          更新時間
          {getStockSortIcon('updateTime')}
        </div>
      ),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 150,
      sorter: true,
      render: (text: string) => <Text type="secondary">{text}</Text>
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('status')}
        >
          狀態
          {getStockSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => {
        const statusConfig = {
          normal: { color: 'green', text: '正常' },
          low_stock: { color: 'orange', text: '庫存不足' },
          out_of_stock: { color: 'red', text: '缺貨' },
          excess: { color: 'blue', text: '庫存過多' }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    actions: {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right' as 'right',
      render: (__unused: unknown, record: StockItem) => (
        <Space>
          <Tooltip title="查看詳情">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            >
              詳情
            </Button>
          </Tooltip>
          <Tooltip title="編輯">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleStockAdjustment(record)}
            >
              編輯
            </Button>
          </Tooltip>
        </Space>
      )
    }
  };

  // 根據列配置生成顯示的列
  const columns: ColumnsType<StockItem> = Object.entries(stockColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allStockColumns[key as keyof typeof allStockColumns];
      if (!column) {
        console.warn(`Missing stock column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 使庫存表支持拖拽列排序（排除 操作 列）
  const { columns: draggableStockColumns, order: stockOrder } = useDraggableColumns<StockItem>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'inventory-stock-table',
  });

  // 拖拽後與原有列配置 order 同步，確保持久化一致
  useEffect(() => {
    if (!stockOrder || stockOrder.length === 0) return;
    setStockColumnSettings((prev: StockColumnSettings) => {
      const next: StockColumnSettings = { ...prev } as StockColumnSettings;
      stockOrder.forEach((k, idx) => {
        if ((next as any)[k]) (next as any)[k] = { ...(next as any)[k], order: idx };
      });
      (Object.keys(next) as Array<keyof StockColumnSettings>).forEach((k) => {
        if (!stockOrder.includes(k)) {
          const o = (next as any)[k]?.order ?? 9999;
          (next as any)[k] = { ...(next as any)[k], order: Math.max(stockOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('stock-management-columns-config', JSON.stringify(next));
        localStorage.setItem('stock-management-columns-version', '2024-01-v2');
      } catch {}
      return next;
    });
  }, [stockOrder]);

  // 處理庫存列可見性切換
  const handleStockColumnVisibilityChange = (columnKey: StockColumnKey, visible: boolean) => {
    const newSettings: StockColumnSettings = {
      ...(stockColumnSettings as StockColumnSettings),
      [columnKey]: { ...(stockColumnSettings as any)[columnKey], visible }
    } as StockColumnSettings;
    setStockColumnSettings(newSettings);
    localStorage.setItem('stock-management-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('stock-management-columns-version', '2024-01-v3');
  };

  // 顯示所有庫存列
  const showAllStockColumns = () => {
    const allVisibleSettings = {
      sku: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      barcode: { visible: true, order: 2 },
      stockQuantity: { visible: true, order: 3 },
      availableQuantity: { visible: true, order: 4 },
      outboundQuantity: { visible: true, order: 5 },
      expectedQuantity: { visible: true, order: 6 },
      occupiedCBM: { visible: true, order: 7 },
      lastInboundOrder: { visible: true, order: 8 },
      lastOutboundOrder: { visible: true, order: 9 },
      updateTime: { visible: true, order: 10 },
      status: { visible: true, order: 11 },
      actions: { visible: true, order: 12 }
    };
    setStockColumnSettings(allVisibleSettings);
    localStorage.setItem('stock-management-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('stock-management-columns-version', '2024-01-v4');
    message.success('所有列已顯示');
  };

  // 重置庫存列配置
  const resetStockColumnSettings = () => {
    const defaultSettings = {
      sku: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      barcode: { visible: true, order: 2 },
      stockQuantity: { visible: true, order: 3 },
      availableQuantity: { visible: true, order: 4 },
      outboundQuantity: { visible: true, order: 5 },
      expectedQuantity: { visible: true, order: 6 },
      occupiedCBM: { visible: false, order: 7 },
      lastInboundOrder: { visible: false, order: 8 },
      lastOutboundOrder: { visible: false, order: 9 },
      updateTime: { visible: true, order: 10 },
      status: { visible: true, order: 11 },
      actions: { visible: true, order: 12 }
    };
    setStockColumnSettings(defaultSettings);
    localStorage.setItem('stock-management-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('stock-management-columns-version', '2024-01-v4');
    message.success('列配置已重置');
  };

  // 庫存列設置菜單
  const stockColumnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>列設置</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allStockColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleStockColumnVisibilityChange(key as StockColumnKey, !((stockColumnSettings as any)[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={(stockColumnSettings as any)[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleStockColumnVisibilityChange(key as StockColumnKey, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title : 
               key === 'sku' ? 'SKU' :
               key === 'productName' ? '產品名稱' :
               key === 'barcode' ? '條碼' :
               key === 'stockQuantity' ? '庫存數量' :
               key === 'availableQuantity' ? '可下單數量' :
               key === 'outboundQuantity' ? '出庫中數量' :
               key === 'occupiedCBM' ? '佔用CBM' :
               key === 'lastInboundOrder' ? '最後入庫單' :
               key === 'lastOutboundOrder' ? '最後發貨單' :
               key === 'updateTime' ? '更新時間' :
               key === 'status' ? '狀態' :
               key === 'actions' ? '操作' : key}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 0' }}>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            showAllStockColumns();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            顯示所有列
          </Button>
        </div>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            resetStockColumnSettings();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            重置列設置
          </Button>
        </div>
      </div>
    </div>
  );

  // 處理庫存數據排序
  const sortedAndFilteredStockData = useMemo(() => {
    let result = [...filteredData];
    
    if (stockSortField && stockSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[stockSortField as keyof StockItem];
        let bValue: any = b[stockSortField as keyof StockItem];
        
        // 處理數字字段排序
        if (['stockQuantity', 'availableQuantity', 'outboundQuantity', 'occupiedCBM'].includes(stockSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return stockSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return stockSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filteredData, stockSortField, stockSortOrder]);

  // 相關訂單表格列
  const orderColumns: ColumnsType<RelatedOrder> = [
    {
      title: '訂單號',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string) => <Text code>{text}</Text>
    },
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeConfig = {
          inbound: { color: 'green', text: '入庫', icon: <InboxOutlined /> },
          outbound: { color: 'blue', text: '出庫', icon: <SendOutlined /> },
          transfer: { color: 'orange', text: '調撥', icon: <BarChartOutlined /> }
        };
        const config = typeConfig[type as keyof typeof typeConfig];
        return (
          <Space>
            {config.icon}
            <Tag color={config.color}>{config.text}</Tag>
          </Space>
        );
      }
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date'
    },
    {
      title: '數量',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag>{status}</Tag>
    },
    {
      title: '參考號',
      dataIndex: 'reference',
      key: 'reference',
      render: (text: string) => text ? <Text code>{text}</Text> : '-'
    }
  ];

  // 定義所有可用的調整申請列
  const allAdjustmentColumns = {
    sku: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('sku')}
        >
          SKU
          {getAdjustmentSortIcon('sku')}
        </div>
      ),
      dataIndex: 'sku',
      key: 'sku',
      width: 220,
      fixed: 'left' as 'left',
      sorter: true,
      render: (text: string) => <Text code strong>{text}</Text>
    },
    productName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('productName')}
        >
          產品名稱
          {getAdjustmentSortIcon('productName')}
        </div>
      ),
      dataIndex: 'productName',
      key: 'productName',
      width: 180,
      ellipsis: { showTitle: false },
      sorter: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      )
    },
    currentQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('currentQuantity')}
        >
          當前庫存
          {getAdjustmentSortIcon('currentQuantity')}
        </div>
      ),
      dataIndex: 'currentQuantity',
      key: 'currentQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => <Text strong>{value}</Text>
    },
    adjustmentQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('adjustmentQuantity')}
        >
          調整數量
          {getAdjustmentSortIcon('adjustmentQuantity')}
        </div>
      ),
      dataIndex: 'adjustmentQuantity',
      key: 'adjustmentQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => (
        <Text strong style={{ color: value > 0 ? '#52c41a' : '#ff4d4f' }}>
          {value > 0 ? '+' : ''}{value}
        </Text>
      )
    },
    newQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('newQuantity')}
        >
          調整後
          {getAdjustmentSortIcon('newQuantity')}
        </div>
      ),
      dataIndex: 'newQuantity',
      key: 'newQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => <Text>{value}</Text>
    },
    reason: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reason')}
        >
          調整原因
          {getAdjustmentSortIcon('reason')}
        </div>
      ),
      dataIndex: 'reason',
      key: 'reason',
      width: 120,
      sorter: true,
      render: (text: string) => <Tag>{text}</Tag>
    },
    notes: {
      title: '備註',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      ellipsis: { showTitle: false },
      render: (text: string) => text ? (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ) : '-'
    },
    requestedBy: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('requestedBy')}
        >
          申請人
          {getAdjustmentSortIcon('requestedBy')}
        </div>
      ),
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      width: 100,
      sorter: true,
      render: (text: string) => (
        <Space>
          <UserOutlined />
          <Text>{text}</Text>
        </Space>
      )
    },
    requestedAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('requestedAt')}
        >
          申請時間
          {getAdjustmentSortIcon('requestedAt')}
        </div>
      ),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 150,
      sorter: true,
      render: (text: string) => (
        <Space>
          <CalendarOutlined />
          <Text type="secondary">{text}</Text>
        </Space>
      )
    },
    reviewedBy: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reviewedBy')}
        >
          審核人
          {getAdjustmentSortIcon('reviewedBy')}
        </div>
      ),
      dataIndex: 'reviewedBy',
      key: 'reviewedBy',
      width: 100,
      sorter: true,
      render: (text: string) => text ? (
        <Space>
          <UserOutlined />
          <Text>{text}</Text>
        </Space>
      ) : '-'
    },
    reviewedAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reviewedAt')}
        >
          審核時間
          {getAdjustmentSortIcon('reviewedAt')}
        </div>
      ),
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      width: 150,
      sorter: true,
      render: (text: string) => text ? (
        <Space>
          <CalendarOutlined />
          <Text type="secondary">{text}</Text>
        </Space>
      ) : '-'
    },
    urgency: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('urgency')}
        >
          緊急度
          {getAdjustmentSortIcon('urgency')}
        </div>
      ),
      dataIndex: 'urgency',
      key: 'urgency',
      width: 100,
      sorter: true,
      render: (urgency: string) => {
        const urgencyConfig = {
          low: { color: 'green', text: '低' },
          normal: { color: 'blue', text: '普通' },
          high: { color: 'orange', text: '高' },
          critical: { color: 'red', text: '緊急' }
        };
        const config = urgencyConfig[urgency as keyof typeof urgencyConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('status')}
        >
          狀態
          {getAdjustmentSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => {
        const statusConfig = {
          pending: { color: 'orange', text: '待審核', icon: <ClockCircleOutlined /> },
          approved: { color: 'green', text: '已批準', icon: <CheckCircleOutlined /> },
          rejected: { color: 'red', text: '已拒絕', icon: <CloseCircleOutlined /> }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <Space>
            {config.icon}
            <Tag color={config.color}>{config.text}</Tag>
          </Space>
        );
      }
    },
    actions: {
      title: '操作',
      key: 'actions',
      width: 280,
      fixed: 'right' as 'right',
      render: (__unused: unknown, record: StockAdjustment) => (
        <Space>
          <Tooltip title="查看詳情">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewAdjustmentDetails(record)}
            >
              詳情
            </Button>
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Popconfirm
                title="確認批準此調整申請？"
                onConfirm={() => handleApproval(record, 'approve')}
                okText="確認"
                cancelText="取消"
              >
                <Tooltip title="批準">
                  <Button
                    type="default"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={{ color: '#52c41a' }}
                  >
                    批準
                  </Button>
                </Tooltip>
              </Popconfirm>
              <Tooltip title="拒絕">
                <Button
                  type="default"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  style={{ color: '#ff4d4f' }}
                  onClick={() => handleApproval(record, 'reject')}
                >
                  拒絕
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  };

  // 根據列配置生成顯示的調整申請列
  const adjustmentColumns: ColumnsType<StockAdjustment> = Object.entries(adjustmentColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allAdjustmentColumns[key as keyof typeof allAdjustmentColumns];
      if (!column) {
        console.warn(`Missing adjustment column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 使調整表支持拖拽列排序（排除 操作 列）
  const { columns: draggableAdjustmentColumns, order: adjustmentOrder } = useDraggableColumns<StockAdjustment>({
    columns: adjustmentColumns as any,
    disableDragKeys: ['actions'],
    storageKey: 'inventory-adjustment-table',
  });

  // 拖拽後同步回列配置
  useEffect(() => {
    if (!adjustmentOrder || adjustmentOrder.length === 0) return;
    setAdjustmentColumnSettings((prev: AdjustmentColumnSettings) => {
      const next: AdjustmentColumnSettings = { ...prev } as AdjustmentColumnSettings;
      adjustmentOrder.forEach((k, idx) => {
        if ((next as any)[k]) (next as any)[k] = { ...(next as any)[k], order: idx };
      });
      (Object.keys(next) as Array<keyof AdjustmentColumnSettings>).forEach((k) => {
        if (!adjustmentOrder.includes(k)) {
          const o = (next as any)[k]?.order ?? 9999;
          (next as any)[k] = { ...(next as any)[k], order: Math.max(adjustmentOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(next));
        localStorage.setItem('adjustment-audit-columns-version', '2024-01-v2');
      } catch {}
      return next;
    });
  }, [adjustmentOrder]);

  // 處理調整申請列可見性切換
  const handleAdjustmentColumnVisibilityChange = (columnKey: AdjustmentColumnKey, visible: boolean) => {
    const newSettings: AdjustmentColumnSettings = {
      ...(adjustmentColumnSettings as AdjustmentColumnSettings),
      [columnKey]: { ...(adjustmentColumnSettings as any)[columnKey], visible }
    } as AdjustmentColumnSettings;
    setAdjustmentColumnSettings(newSettings);
    localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('adjustment-audit-columns-version', '2024-01-v2');
  };

  // 顯示所有調整申請列
  const showAllAdjustmentColumns = () => {
    const allVisibleSettings = {
      sku: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      currentQuantity: { visible: true, order: 2 },
      adjustmentQuantity: { visible: true, order: 3 },
      newQuantity: { visible: true, order: 4 },
      reason: { visible: true, order: 5 },
      notes: { visible: true, order: 6 },
      requestedBy: { visible: true, order: 7 },
      requestedAt: { visible: true, order: 8 },
      reviewedBy: { visible: true, order: 9 },
      reviewedAt: { visible: true, order: 10 },
      urgency: { visible: true, order: 11 },
      status: { visible: true, order: 12 },
      actions: { visible: true, order: 13 }
    };
    setAdjustmentColumnSettings(allVisibleSettings);
    localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('adjustment-audit-columns-version', '2024-01-v2');
    message.success('所有列已顯示');
  };

  // 重置調整申請列配置
  const resetAdjustmentColumnSettings = () => {
    const defaultSettings = {
      sku: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      currentQuantity: { visible: true, order: 2 },
      adjustmentQuantity: { visible: true, order: 3 },
      newQuantity: { visible: true, order: 4 },
      reason: { visible: true, order: 5 },
      notes: { visible: false, order: 6 },
      requestedBy: { visible: true, order: 7 },
      requestedAt: { visible: true, order: 8 },
      reviewedBy: { visible: false, order: 9 },
      reviewedAt: { visible: false, order: 10 },
      urgency: { visible: true, order: 11 },
      status: { visible: true, order: 12 },
      actions: { visible: true, order: 13 }
    };
    setAdjustmentColumnSettings(defaultSettings);
    localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('adjustment-audit-columns-version', '2024-01-v2');
    message.success('列配置已重置');
  };

  // 調整申請列設置菜單
  const adjustmentColumnSettingsMenu = (
    <Menu>
      <Menu.Item key="header" disabled>
        <Text strong>列設置</Text>
      </Menu.Item>
      <Menu.Divider />
      {Object.entries(allAdjustmentColumns).map(([key, column]) => (
        <Menu.Item key={key}>
          <Checkbox
            checked={(adjustmentColumnSettings as any)[key]?.visible ?? true}
            onChange={(e) => handleAdjustmentColumnVisibilityChange(key as AdjustmentColumnKey, e.target.checked)}
          >
            {typeof column.title === 'string' ? column.title : 
             key === 'sku' ? 'SKU' :
             key === 'productName' ? '產品名稱' :
             key === 'currentQuantity' ? '當前庫存' :
             key === 'adjustmentQuantity' ? '調整數量' :
             key === 'newQuantity' ? '調整後' :
             key === 'reason' ? '調整原因' :
             key === 'notes' ? '備註' :
             key === 'requestedBy' ? '申請人' :
             key === 'requestedAt' ? '申請時間' :
             key === 'reviewedBy' ? '審核人' :
             key === 'reviewedAt' ? '審核時間' :
             key === 'urgency' ? '緊急度' :
             key === 'status' ? '狀態' :
             key === 'actions' ? '操作' : key}
          </Checkbox>
        </Menu.Item>
      ))}
      <Menu.Divider />
      <Menu.Item key="showAll" onClick={showAllAdjustmentColumns}>
        <Button type="link" size="small" style={{ padding: 0 }}>
          顯示所有列
        </Button>
      </Menu.Item>
      <Menu.Item key="reset" onClick={resetAdjustmentColumnSettings}>
        <Button type="link" size="small" style={{ padding: 0 }}>
          重置列設置
        </Button>
      </Menu.Item>
    </Menu>
  );

  // 處理調整申請數據排序
  const sortedAndFilteredAdjustments = useMemo(() => {
    let result = [...adjustments];
    
    if (adjustmentSortField && adjustmentSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[adjustmentSortField as keyof StockAdjustment];
        let bValue: any = b[adjustmentSortField as keyof StockAdjustment];
        
        // 處理數字字段排序
        if (['currentQuantity', 'adjustmentQuantity', 'newQuantity'].includes(adjustmentSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return adjustmentSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return adjustmentSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [adjustments, adjustmentSortField, adjustmentSortOrder]);

  // 統計數據
  const statistics = {
    totalItems: stockData.length,
    totalValue: stockData.reduce((sum, item) => sum + item.totalValue, 0),
    lowStockItems: stockData.filter(item => item.status === 'low_stock').length,
    outOfStockItems: stockData.filter(item => item.status === 'out_of_stock').length,
    totalCBM: stockData.reduce((sum, item) => sum + item.occupiedCBM, 0)
  };

  // Stock Adjustment統計數據
  const adjustmentStatistics = {
    total: adjustments.length,
    pending: adjustments.filter(item => item.status === 'pending').length,
    approved: adjustments.filter(item => item.status === 'approved').length,
    rejected: adjustments.filter(item => item.status === 'rejected').length,
    critical: adjustments.filter(item => item.urgency === 'critical').length
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 標題和展開收起按鈕 */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>庫存管理</Title>
          <Text type="secondary">管理和監控倉庫庫存狀態及調整申請</Text>
        </div>
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={toggleCollapsed}
          style={{ fontSize: 16 }}
        />
      </div>

      {/* KPI 儀表板（根據當前Tab切換） */}
      {!collapsed && (
        activeTab === 'stock' ? (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="總SKU數" value={statistics.totalItems} prefix={<StockOutlined />} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="總庫存價值" value={`$${statistics.totalValue.toFixed(2)}`} prefix="$" />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="庫存不足" value={statistics.lowStockItems} prefix={<BarChartOutlined />} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="總佔用CBM" value={`${statistics.totalCBM.toFixed(2)}m³`} prefix={<BarChartOutlined />} />
              </Card>
            </Col>
          </Row>
        ) : (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="總申請數" value={adjustmentStatistics.total} prefix={<AuditOutlined />} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="待審核" value={adjustmentStatistics.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="已批準" value={adjustmentStatistics.approved} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title="緊急申請" value={adjustmentStatistics.critical} prefix={<ExclamationCircleOutlined />} valueStyle={{ color: '#ff4d4f' }} />
              </Card>
            </Col>
          </Row>
        )
      )}
      {/* 已移除頂部刷新操作欄，導出功能移至下方操作欄 */}

      {/* 標籤頁 */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={
          <span>
            <StockOutlined />
            庫存管理
          </span>
        } key="stock">
          {/* 庫存篩選欄 */}
          <Card style={{ marginBottom: '20px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Search
                  placeholder="搜索SKU、產品名稱或條碼"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={setSearchText}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="庫存狀態"
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                >
                  <Option value="all">全部狀態</Option>
                  <Option value="normal">正常</Option>
                  <Option value="low_stock">庫存不足</Option>
                  <Option value="out_of_stock">缺貨</Option>
                  <Option value="excess">庫存過多</Option>
                </Select>
              </Col>
            </Row>
          </Card>

          {/* 操作栏 */}
          <Card style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>庫存記錄：{stockTotal} 條</Text>
              </div>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => loadStock(1, 20)}
                >
                  刷新數據
                </Button>
                <Button 
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  type="default"
                >
                  導出
                </Button>
                <Dropdown 
                  overlay={stockColumnSettingsMenu} 
                  trigger={['click']}
                  open={stockColumnSettingsVisible}
                  onOpenChange={setStockColumnSettingsVisible}
                >
                  <Button icon={<SettingOutlined />} title="列設置">
                    列設置
                  </Button>
                </Dropdown>
              </Space>
            </div>
          </Card>

          {/* 庫存表格 */}
          <Card>
            <Table
              columns={draggableStockColumns as any}
              dataSource={sortedAndFilteredStockData}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1800, y: 600 }}
              pagination={{
                current: stockCurrentPage,
                pageSize: stockPageSize,
                total: stockTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條記錄`,
                onChange: (p, ps) => {
                  // 受控分頁：打 API 拉取
                  loadStock(p, ps);
                }
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab={
          <span>
            <AuditOutlined />
            庫存調整審核
            {adjustmentStatistics.pending > 0 && (
              <Badge count={adjustmentStatistics.pending} style={{ marginLeft: 8 }} />
            )}
          </span>
        } key="adjustment">

          {/* 調整申請操作栏 */}
          <Card style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>調整申請：{adjustmentTotal} 條</Text>
                {selectedAdjustmentIds.length > 0 && (
                  <Text type="secondary" style={{ marginLeft: 16 }}>
                    已選擇 {selectedAdjustmentIds.length} 項
                  </Text>
                )}
              </div>
              <Space>
                {/* 查詢條件：SKU 與 狀態 */}
                <Input
                  allowClear
                  placeholder="按 SKU/名稱 查詢"
                  prefix={<SearchOutlined />}
                  value={adjustmentSearchSku}
                  onChange={(e) => setAdjustmentSearchSku(e.target.value)}
                  style={{ width: 220 }}
                />
                <Select
                  value={adjustmentStatus}
                  style={{ width: 140 }}
                  onChange={setAdjustmentStatus}
                >
                  <Option value="all">全部狀態</Option>
                  <Option value="pending">待審核</Option>
                  <Option value="approved">已批准</Option>
                  <Option value="rejected">已拒絕</Option>
                </Select>

                {selectedAdjustmentIds.length > 0 && (
                  <>
                    <Button 
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleBatchApproval('approve')}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      批量批準 ({selectedAdjustmentIds.length})
                    </Button>
                    <Button 
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => handleBatchApproval('reject')}
                    >
                      批量拒絕 ({selectedAdjustmentIds.length})
                    </Button>

                  </>
                )}
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => loadAdjustments(1, 20)}
                >
                  刷新數據
                </Button>
                <Dropdown overlay={adjustmentColumnSettingsMenu} trigger={["click"]}>
                  <Button icon={<SettingOutlined />} title="列設置">
                    列設置
                  </Button>
                </Dropdown>
              </Space>
            </div>
          </Card>

          {/* 調整申請表格 */}
          <Card>
            <Table
              columns={draggableAdjustmentColumns as any}
              dataSource={sortedAndFilteredAdjustments}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1600, y: 600 }}
              rowSelection={{
                selectedRowKeys: selectedAdjustmentIds,
                onChange: handleBatchSelectionChange,
                getCheckboxProps: (record: StockAdjustment) => ({
                  disabled: record.status !== 'pending',
                  name: record.id,
                }),
              }}
              pagination={{
                current: adjustmentCurrentPage,
                pageSize: adjustmentPageSize,
                total: adjustmentTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條記錄`,
                onChange: (p, ps) => {
                  loadAdjustments(p, ps);
                }
              }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 庫存詳情Modal */}
      <Modal
        title={
          <Space>
            <StockOutlined />
            <span>庫存詳情 - {selectedStock?.sku}</span>
          </Space>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        closable
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            關閉
          </Button>
        ]}
        width={1000}
      >
        {selectedStock && (
          <Tabs defaultActiveKey="details">
            <TabPane tab="基本信息" key="details">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="SKU">
                  {[selectedStock.sku, ...(selectedStock.alternateSkuList || [])].filter(Boolean).join(', ')}
                </Descriptions.Item>
                <Descriptions.Item label="條碼">{selectedStock.barcode}</Descriptions.Item>
                <Descriptions.Item label="產品名稱" span={2}>{selectedStock.productName}</Descriptions.Item>
                <Descriptions.Item label="貨主賬號">{selectedStock.ownerAccount}</Descriptions.Item>
                <Descriptions.Item label="類別">{selectedStock.category || '-'}</Descriptions.Item>
                <Descriptions.Item label="庫存數量（總計）">
                  <Text strong style={{ fontSize: '16px' }}>{selectedStock.stockQuantity}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="可下單數量（總計）">
                  <Text style={{ fontSize: '16px' }}>{selectedStock.availableQuantity}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="出庫中數量">{selectedStock.outboundQuantity}</Descriptions.Item>
                <Descriptions.Item label="佔用CBM">{selectedStock.occupiedCBM} m³</Descriptions.Item>
                <Descriptions.Item label="單價">${selectedStock.unitPrice}</Descriptions.Item>
                <Descriptions.Item label="總價值">${selectedStock.totalValue}</Descriptions.Item>
                <Descriptions.Item label="最後入庫單">{selectedStock.lastInboundOrder}</Descriptions.Item>
                <Descriptions.Item label="最後入庫數量">{selectedStock.lastInboundQuantity}</Descriptions.Item>
                <Descriptions.Item label="最後發貨單">{selectedStock.lastOutboundOrder}</Descriptions.Item>
                <Descriptions.Item label="最後發貨數量">{selectedStock.lastOutboundQuantity}</Descriptions.Item>
                <Descriptions.Item label="更新時間" span={2}>{selectedStock.updateTime}</Descriptions.Item>
              </Descriptions>

              {/* 各倉庫庫存分佈 */}
              {selectedStock.warehouses && selectedStock.warehouses.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Title level={5}>各倉庫庫存</Title>
                  <Table
                    dataSource={selectedStock.warehouses}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: '倉庫',
                        dataIndex: 'warehouseName',
                        key: 'warehouseName',
                        render: (text: string) => <Tag color="blue">{text}</Tag>
                      },
                      {
                        title: '庫存數量',
                        dataIndex: 'total',
                        key: 'total',
                        align: 'right' as const,
                        render: (value: number) => <Text strong>{value}</Text>
                      },
                      {
                        title: '可用數量',
                        dataIndex: 'available',
                        key: 'available',
                        align: 'right' as const
                      },
                      {
                        title: '出庫中',
                        dataIndex: 'shipping',
                        key: 'shipping',
                        align: 'right' as const
                      },
                      {
                        title: '貨位',
                        dataIndex: 'locationCode',
                        key: 'locationCode',
                        render: (text: string) => text || '-'
                      },
                      {
                        title: '更新時間',
                        dataIndex: 'updatedAt',
                        key: 'updatedAt',
                        render: (text: string) => text ? new Date(text).toLocaleString('zh-TW') : '-'
                      }
                    ]}
                  />
                </div>
              )}
              
              {/* 貨位分佈 */}
              {productLocations.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Title level={5}>貨位分佈</Title>
                  <Table
                    dataSource={productLocations}
                    rowKey={(record, index) => `${record.orderNumber}-${index}`}
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: '入庫單號',
                        dataIndex: 'orderNumber',
                        key: 'orderNumber',
                        width: 150
                      },
                      {
                        title: '批次',
                        dataIndex: 'lotNumber',
                        key: 'lotNumber',
                        width: 120,
                        render: (text: string) => text || '-'
                      },
                      {
                        title: '到期日',
                        dataIndex: 'expirationDate',
                        key: 'expirationDate',
                        width: 120,
                        render: (date: string) => date ? new Date(date).toLocaleDateString('zh-TW') : '-'
                      },
                      {
                        title: '貨位',
                        dataIndex: 'locationPath',
                        key: 'locationPath',
                        render: (text: string) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>
                      },
                      {
                        title: '數量',
                        dataIndex: 'quantity',
                        key: 'quantity',
                        width: 100,
                        align: 'right' as const,
                        render: (value: number) => <Text strong>{value}</Text>
                      }
                    ]}
                  />
                </div>
              )}
            </TabPane>
            
            <TabPane tab="相關訂單" key="orders">
              <Table
                columns={orderColumns}
                dataSource={relatedOrders}
                rowKey="id"
                size="small"
                pagination={false}
              />
            </TabPane>
          </Tabs>
        )}
      </Modal>

      {/* 庫存調整Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>庫存調整申請</span>
          </Space>
        }
        open={adjustmentVisible}
        onCancel={() => {
          setAdjustmentVisible(false);
          adjustmentForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="庫存調整申請"
          description={
            <div>
              <p><strong>調整流程：</strong>製作者提交 → 核對者審核 → 系統執行</p>
              <p><strong>操作說明：</strong></p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>正數</strong>：增加庫存 (如 +10 表示增加10個)</li>
                <li><strong>負數</strong>：減少庫存 (如 -5 表示減少5個)</li>
                <li>系統會自動計算調整後的數量</li>
                <li>所有調整都需要經過審核才能生效</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
        
        <Form
          form={adjustmentForm}
          layout="vertical"
          onFinish={handleAdjustmentSubmit}
        >
          <Form.Item label="SKU" name="sku">
            <Input disabled />
          </Form.Item>
          
          <Form.Item label="產品名稱" name="productName">
            <Input disabled />
          </Form.Item>
          
          <Form.Item label="當前庫存" name="currentQuantity">
            <InputNumber disabled style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item 
            label="調整數量" 
            name="adjustmentQuantity"
            rules={[
              { required: true, message: '請輸入調整數量' },
              { type: 'number', message: '請輸入有效數字' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const currentQty = getFieldValue('currentQuantity') || 0;
                  
                  if (value < 0 && Math.abs(value) > currentQty) {
                    return Promise.reject(new Error(`減少數量不能超過當前庫存 ${currentQty}`));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            help={
              <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                <div>• 正數 = 增加庫存 (例如: +10 表示增加10個)</div>
                <div>• 負數 = 減少庫存 (例如: -5 表示減少5個)</div>
              </div>
            }
          >
            <InputNumber 
              style={{ width: '100%' }} 
              precision={0}
              placeholder="正數為增加，負數為減少 (例如: +10 或 -5)"
              onChange={(value) => {
                const currentQty = adjustmentForm.getFieldValue('currentQuantity') || 0;
                const newQty = currentQty + (value || 0);
                adjustmentForm.setFieldsValue({ newQuantity: Math.max(0, newQty) });
              }}
            />
          </Form.Item>
          
          <Form.Item 
            label="調整後數量" 
            name="newQuantity"
          >
            <InputNumber disabled style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item 
            label="調整原因" 
            name="reason"
            rules={[{ required: true, message: '請選擇調整原因' }]}
          >
            <Select placeholder="請選擇調整原因">
              {adjustmentReasons.map(reason => (
                <Option key={reason.value} value={reason.value}>
                  {reason.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item label="備註說明" name="notes">
            <Input.TextArea 
              rows={3} 
              placeholder="請詳細說明調整原因和相關情況"
            />
          </Form.Item>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setAdjustmentVisible(false);
                adjustmentForm.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Stock Adjustment詳情Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>調整申請詳情</span>
          </Space>
        }
        open={adjustmentDetailsVisible}
        onCancel={() => setAdjustmentDetailsVisible(false)}
        closable
        footer={[
          <Button key="close" onClick={() => setAdjustmentDetailsVisible(false)}>
            關閉
          </Button>
        ]}
        width={800}
      >
        {selectedAdjustment && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="SKU">
                {[selectedAdjustment.sku, ...(selectedAdjustment.alternateSkuList || [])].filter(Boolean).join(', ')}
              </Descriptions.Item>
              <Descriptions.Item label="產品名稱">{selectedAdjustment.productName}</Descriptions.Item>
              <Descriptions.Item label="倉庫">{selectedAdjustment.warehouse}</Descriptions.Item>
              <Descriptions.Item label="貨位">{selectedAdjustment.location}</Descriptions.Item>
              <Descriptions.Item label="當前庫存">
                <Text strong style={{ fontSize: '16px' }}>{selectedAdjustment.currentQuantity}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="調整數量">
                <Text strong style={{ 
                  fontSize: '16px',
                  color: selectedAdjustment.adjustmentQuantity > 0 ? '#52c41a' : '#ff4d4f'
                }}>
                  {selectedAdjustment.adjustmentQuantity > 0 ? '+' : ''}{selectedAdjustment.adjustmentQuantity}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="調整後庫存">
                <Text strong style={{ fontSize: '16px' }}>{selectedAdjustment.newQuantity}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="緊急度">
                <Tag color={
                  selectedAdjustment.urgency === 'critical' ? 'red' :
                  selectedAdjustment.urgency === 'high' ? 'orange' :
                  selectedAdjustment.urgency === 'normal' ? 'blue' : 'green'
                }>
                  {selectedAdjustment.urgency === 'critical' ? '緊急' :
                   selectedAdjustment.urgency === 'high' ? '高' :
                   selectedAdjustment.urgency === 'normal' ? '普通' : '低'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="調整原因" span={2}>
                <Tag>{selectedAdjustment.reason}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="備註說明" span={2}>
                {selectedAdjustment.notes || '無'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>審核流程</Divider>
            <Timeline>
              <Timeline.Item color="blue">
                <div>
                  <Text strong>{selectedAdjustment.requestedBy}</Text> 提交申請
                  <br />
                  <Text type="secondary">{selectedAdjustment.requestedAt}</Text>
                </div>
              </Timeline.Item>
              
              {selectedAdjustment.status !== 'pending' && (
                <Timeline.Item 
                  color={selectedAdjustment.status === 'approved' ? 'green' : 'red'}
                >
                  <div>
                    <Text strong>{selectedAdjustment.reviewedBy}</Text> {selectedAdjustment.status === 'approved' ? '批準' : '拒絕'}了申請
                    <br />
                    <Text type="secondary">{selectedAdjustment.reviewedAt}</Text>
                    {selectedAdjustment.approvalReason && (
                      <>
                        <br />
                        <Text>原因：{selectedAdjustment.approvalReason}</Text>
                      </>
                    )}
                  </div>
                </Timeline.Item>
              )}
              
              {selectedAdjustment.status === 'pending' && (
                <Timeline.Item color="gray">
                  <Text type="secondary">等待審核...</Text>
                </Timeline.Item>
              )}
            </Timeline>
          </div>
        )}
      </Modal>

      {/* 審核Modal */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            <span>審核調整申請</span>
          </Space>
        }
        open={approvalVisible}
        onCancel={() => {
          setApprovalVisible(false);
          approvalForm.resetFields();
          // 關閉彈窗後刷新列表，保留當前分頁與查詢條件
          loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);
        }}
        footer={null}
        width={500}
      >
        {selectedAdjustment && (
          <div>
            <Alert
              message={`${selectedAdjustment.sku} - ${selectedAdjustment.productName}`}
              description={`申請調整庫存：${selectedAdjustment.currentQuantity} → ${selectedAdjustment.newQuantity} (${selectedAdjustment.adjustmentQuantity > 0 ? '+' : ''}${selectedAdjustment.adjustmentQuantity})`}
              type="info"
              showIcon
              style={{ marginBottom: 20 }}
            />
            
            <Form
              form={approvalForm}
              layout="vertical"
              onFinish={handleApprovalSubmit}
            >
              <Form.Item name="action" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
              
              <Form.Item 
                label="審核意見" 
                name="reason"
                rules={[{ required: true, message: '請填寫審核意見' }]}
              >
                <Input.TextArea 
                  rows={4} 
                  placeholder="請詳細說明審核意見和理由"
                />
              </Form.Item>
              
              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => {
                    setApprovalVisible(false);
                    approvalForm.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    icon={approvalForm.getFieldValue('action') === 'approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{ 
                      backgroundColor: approvalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f',
                      borderColor: approvalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f'
                    }}
                  >
                    {approvalForm.getFieldValue('action') === 'approve' ? '批準申請' : '拒絕申請'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 批量審核Modal */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            <span>批量審核調整申請</span>
          </Space>
        }
        open={batchApprovalVisible}
        onCancel={() => {
          setBatchApprovalVisible(false);
          batchApprovalForm.resetFields();
          // 關閉彈窗後刷新列表，保留當前分頁與查詢條件
          loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);
        }}
        footer={null}
        width={600}
      >
        <div>
          <Alert
            message={`已選擇 ${selectedAdjustmentIds.length} 個調整申請`}
            description="請填寫審核意見，將應用於所有選中的申請"
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
          
          <Form
            form={batchApprovalForm}
            layout="vertical"
            onFinish={handleBatchApprovalSubmit}
          >
            <Form.Item name="action" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            <Form.Item 
              label="審核意見" 
              name="reason"
              rules={[{ required: true, message: '請填寫審核意見' }]}
            >
              <Input.TextArea 
                rows={4} 
                placeholder="請詳細說明批量審核意見和理由"
              />
            </Form.Item>
            
            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setBatchApprovalVisible(false);
                  batchApprovalForm.resetFields();
                }}>
                  取消
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  icon={batchApprovalForm.getFieldValue('action') === 'approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  style={{ 
                    backgroundColor: batchApprovalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f',
                    borderColor: batchApprovalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f'
                  }}
                >
                  {batchApprovalForm.getFieldValue('action') === 'approve' ? `批量批準 (${selectedAdjustmentIds.length})` : `批量拒絕 (${selectedAdjustmentIds.length})`}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default StockManagement;